import axios, { AxiosInstance } from 'axios';
import {
  T_OrderWithCoupon,
  T_BasketItem,
  T_Order,
  T_Coupon,
  T_CURATED_BUNDLE,
  T_Channel,
  T_CTCartApiResponse,
} from '../types';
import { mapCTCartToOrder, MyAccountV2Service } from '..';
import { ChannelJSONConfigService } from './channelJsonConfig';
import { BACKEND_ENDPOINT, CHANNELS_JSON_CONFIG_URL, GRAPHQL_URL, skuRatings } from '../config';
import { ApolloClient, InMemoryCache, NormalizedCacheObject, createHttpLink, ApolloLink } from '@apollo/client';
import { couponStoreApi } from '../store/couponStore';
import { SentryService } from '../services/sentry';
import { customerStoreApi } from '../store/customerStore';

export class KonakartService {
  private kkAgent: AxiosInstance;
  private ctAgent?: AxiosInstance;
  private sentryService?: SentryService;
  private channelsJSONConfigService: any;
  private myAccountV2Service: MyAccountV2Service;
  private myAccountAgent: AxiosInstance = axios.create({ baseURL: BACKEND_ENDPOINT });
  // Safety link: prevent accidental introspection queries from production clients
  private safetyLink = new ApolloLink((operation, forward) => {
    const body =
      operation.query && operation.query.loc && operation.query.loc.source && operation.query.loc.source.body;
    if (process.env.NODE_ENV === 'production' && body && (body.includes('__schema') || body.includes('__type'))) {
      throw new Error('Introspection queries are disabled in production');
    }
    return forward ? forward(operation) : null;
  });

  private gqlAgent: ApolloClient<NormalizedCacheObject> = new ApolloClient({
    link: this.safetyLink.concat(createHttpLink({ uri: GRAPHQL_URL })),
    cache: new InMemoryCache(),
  });
  private newEndPoint = 'New';
  private updateKkExpiry = (response: any) => {
    customerStoreApi.getState().setKkExpireAt(response?.lastModifiedAt, response?.deleteDaysAfterLastModification);
  };

  constructor(kkAgent: AxiosInstance, ctAgent?: AxiosInstance, sentryService?: SentryService) {
    this.kkAgent = kkAgent;
    this.ctAgent = ctAgent;
    this.sentryService = sentryService;
    this.channelsJSONConfigService = new ChannelJSONConfigService(axios.create({ baseURL: CHANNELS_JSON_CONFIG_URL }));
    this.myAccountV2Service = new MyAccountV2Service(this.myAccountAgent, this.gqlAgent);

    if (this.ctAgent) {
      this.ctAgent.interceptors.request.use(config => {
        return config;
      });

      this.ctAgent.interceptors.response.use(
        response => {
          // API returned HTTP 200, but downstream response has an error status
          if ((response.data?.status && response.data?.status !== '200') || response.data?.code !== '200') {
            this.sentryService?.captureException(
              new Error(`Commerce Tools API Error: ${response.data?.message || 'Unknown downstream error'}`),
              {
                tags: {
                  source: 'CommerceToolsService',
                  status: response.data.status.toString(),
                  method: response.config?.method?.toUpperCase() || 'unknown',
                  endpoint: response.config?.url || 'unknown',
                },
                extra: {
                  // Request information
                  accountNumber: response.config?.data?.accountNumber,
                  customerId: response.config?.data?.customerId,
                  customerType: response.config?.data?.customerType,
                  requestSource: response.config?.data?.source,
                  // API information
                  service: 'CommerceToolsService',
                  url: response.config?.url,
                  method: response.config?.method,
                  request: response.config?.data,
                  response: response?.data,
                  httpStatus: response?.status,
                  downstreamStatus: response.data?.status,
                  errorCode: response.data?.errorCode,
                  correlationId: response.data?.correlationId,
                },
              }
            );
          }
          return response;
        },
        error => {
          this.sentryService?.captureException(error, {
            tags: {
              source: 'CommerceToolsService',
              status: error.response?.status?.toString() || 'unknown',
              method: error.config?.method?.toUpperCase() || 'unknown',
              endpoint: error.config?.url || 'unknown',
            },
            extra: {
              service: 'CommerceToolsService',
              url: error.config?.url,
              method: error.config?.method,
              request: error.config?.data,
              response: error.response?.data,
              status: error.response?.status,
            },
          });
          return Promise.reject(error);
        }
      );
    }
  }

  public getTempCustomerId = async (customerType?: string, source: string = 'web', accountNumber?: string) => {
    const response = await this.ctAgent!.post<{
      result: {
        customerId: string;
      };
      code: string;
      message: string;
      description: null;
    }>('/createCart', {
      source,
      customerType,
      accountNumber,
    });

    this.updateKkExpiry(response.data?.result);
    return response.data?.result?.customerId;
  };
  // overrideProductInfoFromKK - overrides Konakart product details with info from Liferay
  // catalogueType - 001 for backbook, 002 for frontbook, use to fetch products from konakart
  public getAllProduct = async (overrideProductInfoFromKK: boolean, catalogueType?: string) => {
    let result: any[] = [];
    const { data } = await this.ctAgent!.get<any>('/products', {
      params: { channel: 'web' },
    });

    result = data?.result ?? [];

    result.forEach((product: any) => {
      const rating = product?.skuRating ?? skuRatings[product?.code] ?? 0; // Remove Rating Option
      product.sku = product?.code;
      product.quantityInCart = 0;
      product.quantityBought = 0;
      product.rating = rating; // Remove Rating Option
      product.occurenceNumber = null;
      product.price0 = product?.priceIncTax;
      product.image = product?.attributes?.image;
      product.model = product?.attributes?.model;
      product.description = product?.attributes?.description;
      product.price = product?.priceIncTax;
      product.categoryName = product?.category;
      product.custom1 = product?.attributes?.image;
      product.custom2 = product?.upSellCoupon;
      product.custom3 = product?.additionalIcomsCodes;
      product.custom4 = product?.acquisitionOrUpgrade;
      product.custom5 = product?.freeProductCombination;
      product.custom6 = product?.freeProductTermsUrl;
      product.custom7 = product?.boxUpgradeMapping;
      product.custom8 = product?.shortDescription;
      product.custom9 = product?.productCustom9;
      product.custom10 = product?.productCustom10;
      product.depends = product?.dependsOn;
      product.accessories = product?.accessories;
    });

    if (overrideProductInfoFromKK) {
      const [channels, cardSection] = await Promise.all([
        this.channelsJSONConfigService.getChannelConfig(),
        this.myAccountV2Service.getCardContainer({
          usePreview: false,
        }),
      ]);

      for (let product of result) {
        let prod = cardSection.data?.cardContainer.find((d: any) => d?.sku === product.sku);
        product.videoUrl = prod?.videoUrl ?? product?.videoUrl;
        product.horizontalVideoUrl = prod?.horizontalVideoUrl ?? product.horizontalVideoUrl;
        product.horizontalImageUrl = prod?.horizontalImageUrl?.url ?? product.horizontalImageUrl;
        product.custom1 = prod?.mainImageUrl?.url ?? product.custom1;
        product.logoImageUrl = prod?.logoImageUrl?.url ?? product.logoImageUrl;
        product.description = prod?.description && prod?.description !== '' ? prod.description : product.description;
        product.explorePopupCopy = prod?.explorePopupCopy ?? product.explorePopupCopy;
        product.name = prod?.name && prod?.name !== '' ? prod.name : product.name;
        product.contractDescription = prod?.contractDescription ?? product.contractDescription;
        product.showExplorePopup = !!prod?.showExplorePopup;
        product.linkLabel = prod?.linkLabel ?? 'Explore';
        product.linkUrl = prod?.linkUrl ?? product.linkUrl;
        product.iconImageUrl = prod?.iconImageUrl?.map((img: any) => img.iconImageUrl);
        product.popupCards = prod?.popupCards?.map((items: any) => ({
          ...items,
          popupCardIconURL: items.popupCardIconUrl.url,
          popupCardImageURL: items.popupCardImageUrl.url,
        }));
        product.channels = channels.filter((c: T_Channel) => c.sku === product.sku);
        product.iconImageUrlFromJSON = prod?.iconImageUrl?.map((img: any) => img.iconImageUrl) ?? [];
      }
    }

    return result;
  };

  public getOrder = async (customerId: string, source: string = 'web', accountNumber?: string) => {
    const payload = {
      customerId,
      source,
      accountNumber,
      customerType: 'upgrade',
    };
    const { data } = await this.ctAgent!.post<T_CTCartApiResponse>('/getCart', payload);
    this.updateKkExpiry(data.result);
    return mapCTCartToOrder(data.result);
  };

  public getAllBundles = async (): Promise<T_CURATED_BUNDLE[]> => {
    // const bundlesLiferayData = await this.liferayConnectService.getBundles();

    return [];
  };

  // Check coupon whether valid or not
  public checkCouponCode = async (couponCode: string) =>
    (await this.kkAgent.post<boolean>('/checkCoupon/' + couponCode, {})).data;

  public applyCouponCode = async (couponCode: string, customerId: string) =>
    (
      await this.kkAgent.post<T_OrderWithCoupon>('/applyCoupon/' + couponCode, {
        customerId: customerId,
      })
    ).data;

  // Get the offer associated with the product you are trying to add.
  public getCouponCode = async (couponCode: string, customerId: string, productId: number) =>
    (
      await this.kkAgent.post<T_Coupon | undefined>('/getCoupon/' + couponCode, {
        customerId: customerId,
        productId: productId,
      })
    ).data;

  // Get back a coupon object with the coupon code passed
  public getCoupon = async (couponCode: string) => {
    const { ctCoupons } = couponStoreApi.getState();

    const existingCoupon = ctCoupons.find(c => c.couponCode === couponCode);

    const coupon: T_Coupon = existingCoupon ?? {
      name: '',
      id: '',
      couponCode: couponCode,
      custom1: '',
      custom2: '',
      custom3: '',
      custom4: '',
      custom5: '',
      description: '',
    };
    return coupon;
  };

  // // Get back a promotion objects with the coupon code passed
  // public getPromotions = async (couponCode: string) => {
  //   const couponRetrieval = (await this.kkAgent.get<T_PromotionRetrieval>('/getPromotionsPerCoupon/' + couponCode))
  //     .data;
  //   // important to note that in KK a coupon code is not unique, but is implemented in a way that will ensure its unique.
  //   // so the first element if content exists will be the coupon object
  //   return couponRetrieval && couponRetrieval.r ? couponRetrieval.r[0] : undefined;
  // };

  // // Get back promotion objects with coupone from coupon codes
  // public getPromotionsWithCouponFromCouponCode = async (couponCodes: string): Promise<T_PromotionWithCoupon[]> => {
  //   const couponRetrieval = (
  //     await this.kkAgent.get<T_PromotionWithCoupon[]>('/getPromotionsWithCoupon/?coupons=' + couponCodes)
  //   ).data;

  //   return couponRetrieval;
  // };

  public updateOrder = async (
    customerId: string,
    productsOrCodes: any[],
    // productsOrCodes: T_OrderRequest[],
    couponsOrCampaigns?: string[],
    customerType: string = 'upgrade',
    source: string = 'web',
    accountNumber?: string
  ): Promise<T_Order> => {
    const { data } = await this.ctAgent!.put<T_CTCartApiResponse>('/updateCart', {
      customerId,
      codes: productsOrCodes,
      source,
      customerType,
      ...(accountNumber && { accountNumber }),
      campaigns: couponsOrCampaigns,
    });

    this.updateKkExpiry(data.result);
    return mapCTCartToOrder(data.result);
  };

  /**
   * For manage packages - where person has existing promotion
   * @param customerId
   * @param products
   * @param coupons
   */
  public updateCampaignOrder = async (
    customerId: string,
    products: Pick<T_BasketItem, 'productId' | 'quantity' | 'finalPriceIncTax'>[],
    coupons?: string[]
  ): Promise<T_Order> => {
    let { data: order } = await this.kkAgent.post<T_Order>('/updateCampaignOrder', {
      customerId,
      products,
      coupons,
    });

    // sort products in order, large rating comes first
    return {
      ...order,
      orderProducts: order.orderProducts.sort((a, b) => {
        return b.product.rating - a.product.rating;
      }),
    };
  };

  public createCart = async (customerType: string, source: string, accountNumber: string): Promise<T_Order> => {
    const { data } = await this.ctAgent!.post<T_CTCartApiResponse>('/createCart', {
      source,
      customerType,
      accountNumber,
    });
    return mapCTCartToOrder(data.result);
  };
}
